import { useMutation, useQueryClient } from '@tanstack/react-query';

import { roleService } from '@/services/role-service';
import { pollUntilCondition } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { roleMutationKeys, roleQueryKeys } from './query-keys';

/**
 * Hook to revoke a permission from a role by role_permission_id (from list row).
 * Waits until the role permissions list no longer contains the permission, then invalidates.
 */
export function useRevokeRolePermission(tenantId?: UUID, roleId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: roleMutationKeys.revokeRolePermission(tenantId, roleId),
    mutationFn: async (permissionId: UUID) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!roleId) throw new Error('Missing role id');

      await roleService.revokeRolePermission(tenantId, roleId, permissionId);

      // Poll to check if permission was revoked (granted=false)
      await pollUntilCondition(async () => {
        let cursor: string | undefined;
        do {
          const page = await roleService.listRolePermissions(tenantId, roleId, {
            limit: 100,
            next: cursor,
          });
          const perm = page.items.find(
            (p) => p.role_permission_id === permissionId
          );
          if (perm?.granted === false) return true;
          cursor = page.next ?? undefined;
        } while (cursor);
        return false;
      });

      return permissionId;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: roleQueryKeys.permissionListByRole(tenantId, roleId),
        refetchType: 'all',
      });
    },
  });
}
